/**
 * InsightPro™ — Branch Performance Page  v1.0
 *
 * Drill-down: Overview (all branches ranked) → Branch Detail
 * Branch Detail: KPIs, revenue vs purchases trend, top depts, top products
 *
 * Mobile-first. Period-aware. All DAX patterns confirmed working.
 */
import React, { useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { pbiQuery } from '../utils/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const GOLD  = '#c4922a';
const GREEN = '#1d9e75';
const RED   = '#c0392b';
const AMBER = '#d4a017';

function useIsMobile() {
  const [v, set] = React.useState(() => window.innerWidth < 768);
  React.useEffect(() => {
    const fn = () => set(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return v;
}

function getDateRange(period) {
  const now   = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const y = today.getFullYear(), m = today.getMonth()+1, d = today.getDate();
  const dow = today.getDay();
  let startDate, endDate;
  if (period === 'TODAY') {
    startDate=`${y},${m},${d}`; endDate=`${y},${m},${d}`;
  } else if (period === 'YESTERDAY' || period === 'YEST') {
    const yest = new Date(today); yest.setDate(yest.getDate()-1);
    startDate=`${yest.getFullYear()},${yest.getMonth()+1},${yest.getDate()}`; endDate=startDate;
  } else if (period === 'WTD') {
    const s=new Date(today); s.setDate(today.getDate()-(dow===0?6:dow-1));
    startDate=`${s.getFullYear()},${s.getMonth()+1},${s.getDate()}`; endDate=`${y},${m},${d}`;
  } else if (period === 'MTD') {
    startDate=`${y},${m},1`; endDate=`${y},${m},${d}`;
  } else if (period === 'QTD') {
    startDate=`${y},${Math.floor((m-1)/3)*3+1},1`; endDate=`${y},${m},${d}`;
  } else if (period === 'YTD') {
    startDate=`${y},1,1`; endDate=`${y},${m},${d}`;
  } else {
    startDate=`${y},${m},1`; endDate=`${y},${m},${d}`;
  }
  const [sy,sm,sd] = startDate.split(',').map(Number);
  const [ey,em,ed] = endDate.split(',').map(Number);
  return { startDate, endDate, pyStart:`${sy-1},${sm},${sd}`, pyEnd:`${ey-1},${em},${ed}` };
}

function usePbi(dax) {
  const [data,    setData]    = React.useState(null);
  const [loading, setLoading] = React.useState(!!dax);
  const [error,   setError]   = React.useState(null);
  const load = useCallback(async () => {
    if (!dax) { setLoading(false); setError(null); setData(null); return; }
    setLoading(true); setError(null);
    try { const r = await pbiQuery({ dax }); setData(r?.rows || []); }
    catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }, [dax]); // eslint-disable-line
  React.useEffect(() => { load(); }, [load]);
  return { data, loading, error, reload: load };
}

const fmtR = n => {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1_000_000) return `R ${(v/1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000)     return `R ${(v/1_000).toFixed(0)}K`;
  return `R ${v.toFixed(0)}`;
};
const fmtPct = n => n == null ? '—' : `${Number(n)>=0?'+':''}${(Number(n)*100).toFixed(1)}%`;
const fmtN   = n => n == null ? '—' : Number(n).toLocaleString('en-ZA');

function Spinner() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'40px 0' }}>
      <div style={{ width:26, height:26, border:`3px solid rgba(196,146,42,0.2)`, borderTop:`3px solid ${GOLD}`, borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function ErrorBox({ msg, onRetry }) {
  return (
    <div style={{ padding:'24px', textAlign:'center' }}>
      <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:RED, letterSpacing:'0.12em', marginBottom:8 }}>DATA LOAD ERROR</div>
      <div style={{ fontSize:12, color:'var(--page-muted)', marginBottom:16 }}>{msg}</div>
      {onRetry && <button onClick={onRetry} style={{ fontFamily:'var(--font-mono)', fontSize:10, padding:'7px 16px', background:'none', border:`1px solid ${GOLD}`, color:GOLD, cursor:'pointer', borderRadius:2 }}>RETRY</button>}
    </div>
  );
}

function Card({ children, style }) {
  return <div style={{ background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:4, overflow:'hidden', ...style }}>{children}</div>;
}

function CardHeader({ title, subtitle }) {
  return (
    <div style={{ padding:'14px 16px 12px', borderBottom:'1px solid var(--card-border)' }}>
      <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--page-muted)' }}>{title}</div>
      {subtitle && <div style={{ fontSize:11, color:'var(--page-muted)', marginTop:3 }}>{subtitle}</div>}
    </div>
  );
}

function KpiCard({ label, value, sub, subColor, alert }) {
  return (
    <div style={{ background: alert ? 'rgba(192,57,43,0.04)' : 'var(--card)', border:`1px solid ${alert ? 'rgba(192,57,43,0.35)' : 'var(--card-border)'}`, borderRadius:4, padding:'14px 16px' }}>
      <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--page-muted)', marginBottom:8 }}>{label}</div>
      <div style={{ fontFamily:'var(--font-display)', fontSize:20, fontWeight:700, color: alert ? RED : 'var(--page-text)', lineHeight:1, marginBottom:6, wordBreak:'break-word' }}>{value}</div>
      {sub && <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color: subColor || 'var(--page-muted)' }}>{sub}</div>}
    </div>
  );
}

function Breadcrumb({ crumbs, onNavigate }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:20, flexWrap:'wrap' }}>
      {crumbs.map((c, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span style={{ color:'var(--page-muted)', fontSize:12 }}>›</span>}
          <span onClick={() => i < crumbs.length-1 && onNavigate(i)}
            style={{ fontFamily:'var(--font-mono)', fontSize:10, letterSpacing:'0.1em', textTransform:'uppercase',
              color: i === crumbs.length-1 ? GOLD : 'var(--page-muted)',
              cursor: i < crumbs.length-1 ? 'pointer' : 'default',
              textDecoration: i < crumbs.length-1 ? 'underline' : 'none',
              padding:'4px 0', minHeight:44, display:'inline-flex', alignItems:'center',
            }}>{c.label}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'var(--void)', border:'1px solid var(--card-border)', padding:'10px 14px', borderRadius:3 }}>
      <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)', marginBottom:6 }}>{label}</div>
      {payload.map((p,i) => <div key={i} style={{ fontSize:12, color:p.color, marginBottom:2 }}>{p.name}: {fmtR(p.value)}</div>)}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// LEVEL 0 — All branches ranked
// ════════════════════════════════════════════════════════════
function BranchOverview({ period, onDrillBranch }) {
  const isMobile = useIsMobile();
  const { startDate, endDate, pyStart, pyEnd } = getDateRange(period);
  const cyF = `'Transactions'[TXDATE] >= DATE(${startDate}) && 'Transactions'[TXDATE] <= DATE(${endDate})`;
  const pyF = `'Transactions'[TXDATE] >= DATE(${pyStart}) && 'Transactions'[TXDATE] <= DATE(${pyEnd})`;

  const dax = `EVALUATE
    TOPN(31,
      ADDCOLUMNS(
        SUMMARIZE(FILTER('Transactions',${cyF}),'CompanyList'[COMPANY NAME]),
        "Revenue",   CALCULATE(SUM('Transactions'[SALES]),      ${cyF}),
        "LastYear",  CALCULATE(SUM('Transactions'[SALES]),      ${pyF}),
        "Purchases", CALCULATE(SUM('Transactions'[PURCHASES]),  ${cyF}),
        "Units",     CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${cyF}),
        "StockValue",CALCULATE(SUMX('StockBalances','StockBalances'[ONHAND]*RELATED('Stock'[AVRGCOST])))
      ),
      [Revenue], DESC
    )
    ORDER BY [Revenue] DESC`;

  const { data, loading, error, reload } = usePbi(dax);
  const rows        = data || [];
  const totalRev    = rows.reduce((s,r) => s+(r['[Revenue]']||0), 0);
  const totalPur    = rows.reduce((s,r) => s+(r['[Purchases]']||0), 0);
  const totalUnits  = rows.reduce((s,r) => s+(r['[Units]']||0), 0);
  const totalLY     = rows.reduce((s,r) => s+(r['[LastYear]']||0), 0);
  const totalGrowth = totalLY > 0 ? (totalRev - totalLY) / totalLY : null;
  const maxRev      = Math.max(...rows.map(r => r['[Revenue]']||0));

  return (
    <div style={{ minWidth:0, overflowX:'hidden' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.18em', textTransform:'uppercase', color:'var(--page-muted)' }}>Branch Performance</div>
        <div style={{ fontFamily:'var(--font-mono)', fontSize:9, padding:'3px 10px', background:'rgba(26,173,99,0.1)', color:GREEN, borderRadius:2 }}>LIVE</div>
      </div>

      {/* Summary KPIs */}
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap:10, marginBottom:16 }}>
        {loading ? [0,1,2,3].map(i=><div key={i} style={{ background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:4, height:80 }}/>)
          : error ? <div style={{ gridColumn:'1/-1' }}><ErrorBox msg={error} onRetry={reload}/></div>
          : <>
            <KpiCard label="Total Revenue" value={fmtR(totalRev)} sub={`${fmtPct(totalGrowth)} vs prior year`} subColor={totalGrowth >= 0 ? GREEN : RED}/>
            <KpiCard label="Gross Margin" value={totalRev > 0 ? `${((totalRev-totalPur)/totalRev*100).toFixed(1)}%` : '—'} sub="excl. overhead"/>
            <KpiCard label="Units Sold" value={fmtN(totalUnits)} sub={`${rows.length} active branches`}/>
            <KpiCard label="Prior Year" value={fmtR(totalLY)} sub="same period comparison"/>
          </>
        }
      </div>

      {/* Branch table */}
      <Card>
        <CardHeader title={`All Branches — ${period}`} subtitle="Click to drill into a branch"/>
        {loading ? <Spinner/> : error ? <ErrorBox msg={error} onRetry={reload}/> : (
          <>
            {!isMobile && (
              <div style={{ display:'grid', gridTemplateColumns:'28px 1fr 100px 90px 72px 80px 64px 80px', gap:6, padding:'8px 14px', background:'rgba(0,0,0,0.15)', borderBottom:'1px solid var(--card-border)' }}>
                {['','BRANCH','REVENUE','PRIOR YEAR','GROWTH','MARGIN','UNITS','STOCK'].map((h,i) => (
                  <div key={i} style={{ fontFamily:'var(--font-mono)', fontSize:8, letterSpacing:'0.08em', color:'var(--page-muted)', textAlign: i<=1 ? 'left' : 'right' }}>{h}</div>
                ))}
              </div>
            )}
            {rows.map((r, i) => {
              const rev    = r['[Revenue]']    || 0;
              const ly     = r['[LastYear]']   || 0;
              const pur    = r['[Purchases]']  || 0;
              const growth = ly > 0 ? (rev - ly) / ly : null;
              const margin = rev > 0 ? (rev - pur) / rev : null;
              const barW   = maxRev > 0 ? (rev / maxRev) * 100 : 0;

              if (isMobile) return (
                <div key={i} onClick={() => onDrillBranch(r['CompanyList[COMPANY NAME]'])}
                  style={{ padding:'12px 14px', borderBottom:'1px solid var(--card-border)', cursor:'pointer', minHeight:44 }}
                  onTouchStart={e => e.currentTarget.style.background='var(--page)'}
                  onTouchEnd={e => e.currentTarget.style.background=''}
                >
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, minWidth:0, overflow:'hidden' }}>
                      <span style={{ fontFamily:'var(--font-mono)', fontSize:9, color:'var(--page-muted)', flexShrink:0 }}>{String(i+1).padStart(2,'0')}</span>
                      <span style={{ fontSize:13, fontWeight:600, color:'var(--page-text)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{r['CompanyList[COMPANY NAME]']}</span>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
                      <span style={{ fontFamily:'var(--font-display)', fontSize:14, fontWeight:700 }}>{fmtR(rev)}</span>
                      <span style={{ color:'var(--page-muted)', fontSize:14 }}>›</span>
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:12, marginBottom:5 }}>
                    <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color: growth >= 0 ? GREEN : RED }}>{growth != null ? fmtPct(growth) : '—'} YoY</span>
                    <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)' }}>M: {margin != null ? (margin*100).toFixed(1)+'%' : '—'}</span>
                    <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)' }}>{fmtN(r['[Units]'])} u</span>
                  </div>
                  <div style={{ height:3, background:'var(--card-border)', borderRadius:2 }}>
                    <div style={{ height:3, width:`${barW}%`, background:GOLD, borderRadius:2 }}/>
                  </div>
                </div>
              );

              return (
                <div key={i} onClick={() => onDrillBranch(r['CompanyList[COMPANY NAME]'])}
                  style={{ display:'grid', gridTemplateColumns:'28px 1fr 100px 90px 72px 80px 64px 80px', gap:6, padding:'10px 14px', borderBottom:'1px solid var(--card-border)', cursor:'pointer', transition:'background 0.12s', alignItems:'center', minWidth:0 }}
                  onMouseEnter={e => e.currentTarget.style.background='var(--page)'}
                  onMouseLeave={e => e.currentTarget.style.background=''}
                >
                  <div style={{ fontFamily:'var(--font-mono)', fontSize:9, color:'var(--page-muted)' }}>{String(i+1).padStart(2,'0')}</div>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:12, color:'var(--page-text)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', marginBottom:3 }}>{r['CompanyList[COMPANY NAME]']}</div>
                    <div style={{ height:3, background:'var(--card-border)', borderRadius:2 }}>
                      <div style={{ height:3, width:`${barW}%`, background:GOLD, borderRadius:2 }}/>
                    </div>
                  </div>
                  <div style={{ fontFamily:'var(--font-display)', fontSize:12, fontWeight:700, textAlign:'right', whiteSpace:'nowrap' }}>{fmtR(rev)}</div>
                  <div style={{ fontFamily:'var(--font-mono)', fontSize:11, color:'var(--page-muted)', textAlign:'right', whiteSpace:'nowrap' }}>{fmtR(ly)}</div>
                  <div style={{ fontFamily:'var(--font-mono)', fontSize:11, color: growth >= 0 ? GREEN : RED, textAlign:'right', whiteSpace:'nowrap' }}>{growth != null ? fmtPct(growth) : '—'}</div>
                  <div style={{ fontFamily:'var(--font-mono)', fontSize:11, color: margin > 0.3 ? GREEN : margin > 0.1 ? AMBER : RED, textAlign:'right', whiteSpace:'nowrap' }}>{margin != null ? (margin*100).toFixed(1)+'%' : '—'}</div>
                  <div style={{ fontFamily:'var(--font-mono)', fontSize:11, color:'var(--page-muted)', textAlign:'right', whiteSpace:'nowrap' }}>{fmtN(r['[Units]'])}</div>
                  <div style={{ fontFamily:'var(--font-mono)', fontSize:11, color:'var(--page-muted)', textAlign:'right', whiteSpace:'nowrap' }}>{fmtR(r['[StockValue]'])}</div>
                </div>
              );
            })}
          </>
        )}
      </Card>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// BRANCH ITEM SEARCH
// ════════════════════════════════════════════════════════════
function BranchItemSearch({ branch, period, cyF }) {
  const isMobile = useIsMobile();
  const [query,     setQuery]     = React.useState('');
  const [submitted, setSubmitted] = React.useState('');
  const [cono,      setCono]      = React.useState(null);

  // Step 1: resolve branch CONO once on mount
  const conoDax = `EVALUATE SELECTCOLUMNS(FILTER('CompanyList','CompanyList'[COMPANY NAME]="${branch}"),"CONO",'CompanyList'[CONO])`;
  React.useEffect(() => {
    pbiQuery({ dax: conoDax }).then(r => {
      const row = r?.rows?.[0];
      if (row) setCono(row['[CONO]']);
    }).catch(() => {});
  }, [branch]); // eslint-disable-line

  const n = new Date();
  const ytdF = `'Transactions'[TXDATE]>=DATE(${n.getFullYear()},1,1)&&'Transactions'[TXDATE]<=DATE(${n.getFullYear()},${n.getMonth()+1},${n.getDate()})`;

  // Step 2: use CONO to filter StockBalances and Transactions directly
  const searchDax = submitted && cono ? `EVALUATE
    TOPN(15,
      ADDCOLUMNS(
        FILTER('Stock',
          CONTAINSSTRING(LOWER('Stock'[Description]), LOWER("${submitted}")) ||
          CONTAINSSTRING(LOWER('Stock'[StockCode]),   LOWER("${submitted}"))
        ),
        "BranchQty",   CALCULATE(SUM('StockBalances'[ONHAND]),     'StockBalances'[CONO]="${cono}"),
        "BranchVal",   CALCULATE(SUM('StockBalances'[ONHAND]),     'StockBalances'[CONO]="${cono}") * 'Stock'[AVRGCOST],
        "PeriodSales", CALCULATE(SUM('Transactions'[SALES]),      ${cyF}, FILTER('CompanyList','CompanyList'[CONO]="${cono}")),
        "PeriodUnits", CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${cyF}, FILTER('CompanyList','CompanyList'[CONO]="${cono}")),
        "YTDSales",    CALCULATE(SUM('Transactions'[SALES]),      ${ytdF}, FILTER('CompanyList','CompanyList'[CONO]="${cono}"))
      ),
      [BranchQty], DESC
    )
    ORDER BY [BranchQty] DESC` : null;

  const { data, loading, error } = usePbi(searchDax);

  const doSearch = () => { if (query.trim().length > 1) setSubmitted(query.trim()); };
  const handleKey = e => { if (e.key === 'Enter') doSearch(); };

  return (
    <Card style={{ marginBottom:12 }}>
      <div style={{ padding:'12px 14px', borderBottom:'1px solid var(--card-border)', display:'flex', alignItems:'center', gap:10, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
        <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--page-muted)', flexShrink:0 }}>Item Search</div>
        <input
          value={query} onChange={e => setQuery(e.target.value)} onKeyDown={handleKey}
          placeholder={`Search products at ${branch.split(' ').slice(-2).join(' ')}...`}
          style={{ flex:1, minWidth: isMobile ? '100%' : 200, padding:'8px 12px', background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:3, color:'var(--page-text)', fontFamily:'var(--font-body)', fontSize:12, outline:'none' }}
        />
        <button onClick={doSearch} style={{ padding:'8px 18px', background:GOLD, border:'none', borderRadius:3, color:'var(--void)', fontFamily:'var(--font-mono)', fontSize:10, fontWeight:700, cursor:'pointer', flexShrink:0, minHeight:44, minWidth:80 }}>SEARCH</button>
      </div>
      {!!submitted && !!cono && loading && <Spinner/>}
      {!!submitted && !!cono && !loading && !!error && <ErrorBox msg={error}/>}
      {!!submitted && !!cono && !loading && !error && !data?.length && (
        <div style={{ padding:'20px', textAlign:'center', fontFamily:'var(--font-mono)', fontSize:11, color:'var(--page-muted)' }}>No results for "{submitted}" at this branch</div>
      )}
      {!!submitted && !!cono && !loading && !error && (data || []).map((item, i) => {
              const qty = item['[BranchQty]'] || 0;
              const bv  = item['[BranchVal]'] || 0;
              return (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'11px 14px', borderBottom:'1px solid var(--card-border)', minHeight:44, minWidth:0, overflow:'hidden' }}>
                  <div style={{ fontFamily:'var(--font-mono)', fontSize:9, color:'var(--page-muted)', width:18, flexShrink:0 }}>{String(i+1).padStart(2,'0')}</div>
                  <div style={{ flex:1, minWidth:0, overflow:'hidden' }}>
                    <div style={{ fontSize:12, color:'var(--page-text)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{item['Stock[Description]'] || item['Stock[StockCode]']}</div>
                    <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)', marginTop:2 }}>
                      Code: {item['Stock[StockCode]']}
                      {item['[PeriodSales]'] > 0 && ` · ${period} Sales: ${fmtR(item['[PeriodSales]'])} (${fmtN(item['[PeriodUnits]'])} units)`}
                      {item['[YTDSales]'] > 0 && ` · YTD: ${fmtR(item['[YTDSales]'])}`}
                    </div>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ fontFamily:'var(--font-display)', fontSize:13, fontWeight:700, color: qty > 0 ? 'var(--page-text)' : RED, whiteSpace:'nowrap' }}>{fmtN(qty)} units</div>
                    {bv > 0 && <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)', whiteSpace:'nowrap' }}>Stock: {fmtR(bv)}</div>}
                  </div>
                </div>
              );
            })}
    </Card>
  );
}

// ════════════════════════════════════════════════════════════
// LEVEL 1 — Branch Detail
// ════════════════════════════════════════════════════════════
function BranchDetail({ branch, period }) {
  const isMobile = useIsMobile();
  const { startDate, endDate, pyStart, pyEnd } = getDateRange(period);
  const cyF = `'Transactions'[TXDATE] >= DATE(${startDate}) && 'Transactions'[TXDATE] <= DATE(${endDate})`;
  const pyF = `'Transactions'[TXDATE] >= DATE(${pyStart}) && 'Transactions'[TXDATE] <= DATE(${pyEnd})`;
  const BF  = `FILTER('CompanyList','CompanyList'[COMPANY NAME]="${branch}")`;

  const kpiDax = `EVALUATE ROW(
    "Revenue",    CALCULATE(SUM('Transactions'[SALES]),      ${cyF}, ${BF}),
    "LastYear",   CALCULATE(SUM('Transactions'[SALES]),      ${pyF}, ${BF}),
    "Purchases",  CALCULATE(SUM('Transactions'[PURCHASES]),  ${cyF}, ${BF}),
    "Units",      CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${cyF}, ${BF}),
    "StockValue", CALCULATE(SUMX('StockBalances','StockBalances'[ONHAND]*RELATED('Stock'[AVRGCOST])), ${BF}),
    "StockUnits", CALCULATE(SUM('StockBalances'[ONHAND]), ${BF})
  )`;

  const trendDax = `EVALUATE
    CALCULATETABLE(
      ADDCOLUMNS(
        FILTER(
          SUMMARIZE('Transactions','Date'[YYYYMM],'Date'[MonthName],'Date'[Year]),
          'Date'[YYYYMM] >= 202401
        ),
        "Revenue",   CALCULATE(SUM('Transactions'[SALES])),
        "Purchases", CALCULATE(SUM('Transactions'[PURCHASES])),
        "Units",     CALCULATE(SUM('Transactions'[UNITS_SOLD]))
      ),
      ${BF}
    )
    ORDER BY 'Date'[YYYYMM] ASC`;

  const deptDax = `EVALUATE
    TOPN(12,
      CALCULATETABLE(
        ADDCOLUMNS(
          SUMMARIZE('Transactions','Departments'[DeptName]),
          "Revenue",   CALCULATE(SUM('Transactions'[SALES])),
          "Purchases", CALCULATE(SUM('Transactions'[PURCHASES])),
          "Units",     CALCULATE(SUM('Transactions'[UNITS_SOLD]))
        ),
        ${cyF}, ${BF}
      ),
      [Revenue], DESC
    )`;

  const prodDax = `EVALUATE
    TOPN(15,
      CALCULATETABLE(
        ADDCOLUMNS(
          SUMMARIZE('Transactions','Transactions'[CODE],'Stock'[Description]),
          "Revenue", CALCULATE(SUM('Transactions'[SALES])),
          "Units",   CALCULATE(SUM('Transactions'[UNITS_SOLD]))
        ),
        ${cyF}, ${BF}
      ),
      [Revenue], DESC
    )`;

  const { data: kpiRaw, loading: l0, error: e0, reload: r0 } = usePbi(kpiDax);
  const { data: trend,  loading: l1, error: e1, reload: r1 } = usePbi(trendDax);
  const { data: depts,  loading: l2, error: e2, reload: r2 } = usePbi(deptDax);
  const { data: prods,  loading: l3, error: e3, reload: r3 } = usePbi(prodDax);

  const kpi    = Array.isArray(kpiRaw) ? kpiRaw[0] : kpiRaw;
  const rev    = kpi?.['[Revenue]']   || 0;
  const ly     = kpi?.['[LastYear]']  || 0;
  const pur    = kpi?.['[Purchases]'] || 0;
  const growth = ly > 0 ? (rev - ly) / ly : null;
  const margin = rev > 0 ? (rev - pur) / rev : null;

  const trendData = (trend || []).map(r => ({
    label:     `${r['Date[MonthName]']?.slice(0,3)} ${String(r['Date[Year]']).slice(2)}`,
    Revenue:   r['[Revenue]']   || 0,
    Purchases: r['[Purchases]'] || 0,
  }));

  const maxDept = Math.max(...(depts || []).map(d => d['[Revenue]']||0), 1);
  const maxProd = Math.max(...(prods || []).map(p => p['[Revenue]']||0), 1);

  return (
    <div style={{ minWidth:0, overflowX:'hidden' }}>

      {/* KPI strip */}
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap:10, marginBottom:16 }}>
        {l0 ? [0,1,2,3,4,5].map(i=><div key={i} style={{ background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:4, height:80 }}/>)
          : e0 ? <div style={{ gridColumn:'1/-1' }}><ErrorBox msg={e0} onRetry={r0}/></div>
          : <>
            <KpiCard label={`Revenue — ${period}`}  value={fmtR(rev)}   sub={`${growth != null ? fmtPct(growth) : '—'} vs prior year`} subColor={growth >= 0 ? GREEN : RED}/>
            <KpiCard label="Prior Year Same Period"  value={fmtR(ly)}    sub="baseline comparison"/>
            <KpiCard label="Gross Margin"            value={margin != null ? `${(margin*100).toFixed(1)}%` : '—'} sub="excl. overhead" subColor={margin > 0.3 ? GREEN : AMBER}/>
            <KpiCard label="Units Sold"              value={fmtN(kpi?.['[Units]'])} sub={`${period} period`}/>
            <KpiCard label="Stock Value at Cost"     value={fmtR(kpi?.['[StockValue]'])} sub={`${fmtN(kpi?.['[StockUnits]'])} units on hand`}/>
            <KpiCard label="Purchases (Cost of Stock)" value={fmtR(pur)} sub="goods received this period"/>
          </>
        }
      </div>

      {/* Revenue vs Purchases trend */}
      <Card style={{ marginBottom:12 }}>
        <CardHeader title="Revenue vs Purchases — Monthly Trend"/>
        <div style={{ padding:'0 14px 16px' }}>
          {l1 ? <Spinner/> : e1 ? <ErrorBox msg={e1} onRetry={r1}/> : (
            <>
              <div style={{ display:'flex', gap:16, padding:'10px 0 6px', flexWrap:'wrap' }}>
                {[['Revenue', GOLD],['Purchases', GREEN]].map(([k,c]) => (
                  <div key={k} style={{ display:'flex', alignItems:'center', gap:5 }}>
                    <div style={{ width:8, height:8, borderRadius:2, background:c }}/>
                    <span style={{ fontFamily:'var(--font-mono)', fontSize:9, color:'var(--page-muted)' }}>{k}</span>
                  </div>
                ))}
              </div>
              <ResponsiveContainer width="100%" height={isMobile ? 150 : 200}>
                <BarChart data={trendData} barGap={2} barCategoryGap="20%">
                  <XAxis dataKey="label" tick={{ fontFamily:'var(--font-mono)', fontSize:isMobile?8:9, fill:'var(--page-muted)' }} axisLine={false} tickLine={false} interval={isMobile ? 2 : 0}/>
                  <YAxis hide/>
                  <Tooltip content={<ChartTip/>}/>
                  <Bar dataKey="Revenue"   fill={GOLD}  radius={[2,2,0,0]} name="Revenue"/>
                  <Bar dataKey="Purchases" fill={GREEN} radius={[2,2,0,0]} name="Purchases"/>
                </BarChart>
              </ResponsiveContainer>
            </>
          )}
        </div>
      </Card>

      {/* Item Search */}
      <BranchItemSearch branch={branch} period={period} cyF={cyF}/>

      {/* Depts + Products */}
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) minmax(0,1fr)', gap:12 }}>
        <Card>
          <CardHeader title={`Top Departments — ${period}`} subtitle="By revenue"/>
          {l2 ? <Spinner/> : e2 ? <ErrorBox msg={e2} onRetry={r2}/> : (depts || []).map((d, i) => {
            const dRev = d['[Revenue]'] || 0;
            const dPur = d['[Purchases]'] || 0;
            const dMar = dRev > 0 ? (dRev - dPur) / dRev : null;
            const barW = (dRev / maxDept) * 100;
            return (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px', borderBottom:'1px solid var(--card-border)', minHeight:44, minWidth:0, overflow:'hidden' }}>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:9, color:'var(--page-muted)', width:18, flexShrink:0 }}>{String(i+1).padStart(2,'0')}</div>
                <div style={{ flex:1, minWidth:0, overflow:'hidden' }}>
                  <div style={{ fontSize:12, color:'var(--page-text)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{d['Departments[DeptName]']}</div>
                  <div style={{ display:'flex', gap:8, marginTop:2 }}>
                    <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)' }}>{fmtN(d['[Units]'])} units</span>
                    {dMar != null && <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color: dMar > 0.3 ? GREEN : AMBER }}>M: {(dMar*100).toFixed(1)}%</span>}
                  </div>
                  <div style={{ height:2, background:'var(--card-border)', borderRadius:2, marginTop:4 }}>
                    <div style={{ height:2, width:`${barW}%`, background:GOLD, borderRadius:2 }}/>
                  </div>
                </div>
                <div style={{ fontFamily:'var(--font-display)', fontSize:13, fontWeight:600, whiteSpace:'nowrap', flexShrink:0 }}>{fmtR(dRev)}</div>
              </div>
            );
          })}
        </Card>

        <Card>
          <CardHeader title={`Top Products — ${period}`} subtitle="By revenue"/>
          {l3 ? <Spinner/> : e3 ? <ErrorBox msg={e3} onRetry={r3}/> : (prods || []).map((p, i) => {
            const pRev = p['[Revenue]'] || 0;
            const barW = (pRev / maxProd) * 100;
            return (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px', borderBottom:'1px solid var(--card-border)', minHeight:44, minWidth:0, overflow:'hidden' }}>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:9, color:'var(--page-muted)', width:18, flexShrink:0 }}>{String(i+1).padStart(2,'0')}</div>
                <div style={{ flex:1, minWidth:0, overflow:'hidden' }}>
                  <div style={{ fontSize:12, color:'var(--page-text)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p['Stock[Description]'] || p['Transactions[CODE]']}</div>
                  <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)', marginTop:2 }}>{fmtN(p['[Units]'])} units · {p['Transactions[CODE]']}</div>
                  <div style={{ height:2, background:'var(--card-border)', borderRadius:2, marginTop:4 }}>
                    <div style={{ height:2, width:`${barW}%`, background:GOLD, borderRadius:2 }}/>
                  </div>
                </div>
                <div style={{ fontFamily:'var(--font-display)', fontSize:13, fontWeight:600, whiteSpace:'nowrap', flexShrink:0 }}>{fmtR(pRev)}</div>
              </div>
            );
          })}
        </Card>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// MAIN
// ════════════════════════════════════════════════════════════
export default function BranchPage() {
  const { period = 'MTD' } = useOutletContext() || {};
  const [nav, setNav] = React.useState([{ level:0, label:'Branch Performance' }]);
  const current      = nav[nav.length - 1];
  const navigateTo   = crumb => setNav(prev => [...prev, crumb]);
  const navigateBack = idx   => setNav(prev => prev.slice(0, idx + 1));
  const crumbs       = nav.map(n => ({ label: n.label }));

  return (
    <div style={{ animation:'fadeUp 0.4s ease both', minWidth:0, overflowX:'hidden' }}>
      <Breadcrumb crumbs={crumbs} onNavigate={navigateBack}/>
      {current.level === 0 && (
        <BranchOverview key={period} period={period}
          onDrillBranch={branch => navigateTo({ level:1, label: branch, branch })}/>
      )}
      {current.level === 1 && (
        <BranchDetail key={current.branch + period} branch={current.branch} period={period}/>
      )}
    </div>
  );
}
